import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Receipt, Check, X, AlertTriangle, RefreshCw } from 'lucide-react';
import api from '../../utils/api';

const CATEGORIES = [
  "Trainer Salary",
  "Equipment Purchase",
  "Equipment Maintenance",
  "Rent",
  "Electricity / Utilities",
  "Supplies",
  "Other"
];

const ExpensesTab = () => {
  const gymCode = JSON.parse(localStorage.getItem('fitscan_user') || '{}').gymcode;

  const [activeSegment, setActiveSegment] = useState('log'); // 'log', 'pending', 'rejected'
  
  // Log variables
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [logData, setLogData] = useState({ total: 0, categoryBreakdown: {}, entries: [] });
  const [loadingLog, setLoadingLog] = useState(false);

  // Queue variables
  const [queueEntries, setQueueEntries] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Active selected expense for edit/delete
  const [selectedExpense, setSelectedExpense] = useState(null);

  // Form states
  const [formCategory, setFormCategory] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [formNote, setFormNote] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Fetch expense log
  const fetchExpenseLog = async () => {
    if (!gymCode) return;
    setLoadingLog(true);
    try {
      const res = await api.get(`/api/expense/${gymCode}/log?year=${selectedYear}&month=${selectedMonth}`);
      setLogData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingLog(false);
    }
  };

  // Fetch queue entries
  const fetchExpenseQueue = async (status) => {
    if (!gymCode) return;
    setLoadingQueue(true);
    try {
      const res = await api.get(`/api/expense/${gymCode}/queue?status=${status}`);
      setQueueEntries(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingQueue(false);
    }
  };

  useEffect(() => {
    if (activeSegment === 'log') {
      fetchExpenseLog();
    } else if (activeSegment === 'pending') {
      fetchExpenseQueue('Pending');
    } else if (activeSegment === 'rejected') {
      fetchExpenseQueue('Rejected');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymCode, activeSegment, selectedMonth, selectedYear]);

  // Handle Add Submit
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');
    try {
      await api.post(`/api/expense/${gymCode}`, {
        category: formCategory,
        amount: parseFloat(formAmount),
        date: new Date(formDate).toISOString(),
        note: formNote
      });
      setAddModalOpen(false);
      resetForm();
      fetchExpenseLog();
    } catch (err) {
      console.error(err);
      setFormError(err.response?.data?.message || 'Failed to add expense');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle Edit Submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');
    try {
      await api.put(`/api/expense/${gymCode}/${selectedExpense.id}`, {
        category: formCategory,
        amount: parseFloat(formAmount),
        date: new Date(formDate).toISOString(),
        note: formNote
      });
      setEditModalOpen(false);
      resetForm();
      if (activeSegment === 'log') fetchExpenseLog();
      else if (activeSegment === 'pending') fetchExpenseQueue('Pending');
      else if (activeSegment === 'rejected') fetchExpenseQueue('Rejected');
    } catch (err) {
      console.error(err);
      setFormError(err.response?.data?.message || 'Failed to update expense');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle Delete
  const handleDeleteSubmit = async () => {
    setFormSubmitting(true);
    try {
      await api.delete(`/api/expense/${gymCode}/${selectedExpense.id}`);
      setDeleteModalOpen(false);
      setSelectedExpense(null);
      if (activeSegment === 'log') fetchExpenseLog();
      else if (activeSegment === 'pending') fetchExpenseQueue('Pending');
      else if (activeSegment === 'rejected') fetchExpenseQueue('Rejected');
    } catch (err) {
      console.error(err);
    } finally {
      setFormSubmitting(false);
    }
  };

  // Process approval/reject action
  const handleAction = async (id, action) => {
    try {
      await api.post(`/api/expense/${gymCode}/${id}/action`, { action });
      if (activeSegment === 'pending') fetchExpenseQueue('Pending');
      else if (activeSegment === 'rejected') fetchExpenseQueue('Rejected');
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormCategory('');
    setFormAmount('');
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormNote('');
    setFormError('');
    setSelectedExpense(null);
  };

  const openEditModal = (expense) => {
    setSelectedExpense(expense);
    setFormCategory(expense.category);
    setFormAmount(expense.amount.toString());
    setFormDate(new Date(expense.date).toISOString().split('T')[0]);
    setFormNote(expense.note || '');
    setEditModalOpen(true);
  };

  const openDeleteModal = (expense) => {
    setSelectedExpense(expense);
    setDeleteModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header section with add button */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Expenses Manager</h1>
          <p className="text-sm text-gray-400">Track and manage gym outgoings and trainer submissions</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setAddModalOpen(true);
          }}
          className="flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-zinc-950 px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg text-sm cursor-pointer"
        >
          <Plus size={18} />
          <span>Add Expense</span>
        </button>
      </div>

      {/* Segment controls */}
      <div className="flex border border-[#2b2b2b] bg-[#181818] p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveSegment('log')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
            activeSegment === 'log' ? 'bg-[#222] text-lime-400 shadow' : 'text-gray-400 hover:text-white'
          }`}
        >
          Expense Log
        </button>
        <button
          onClick={() => setActiveSegment('pending')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
            activeSegment === 'pending' ? 'bg-[#222] text-lime-400 shadow' : 'text-gray-400 hover:text-white'
          }`}
        >
          Pending Review
        </button>
        <button
          onClick={() => setActiveSegment('rejected')}
          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
            activeSegment === 'rejected' ? 'bg-[#222] text-lime-400 shadow' : 'text-gray-400 hover:text-white'
          }`}
        >
          Rejected
        </button>
      </div>

      {/* Main content conditional rendering */}
      {activeSegment === 'log' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left 2 cols: Log table */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-white text-lg">Transactions</h3>
                {/* Month/Year selector */}
                <div className="flex gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="bg-[#222] border border-[#2b2b2b] text-white px-3 py-1.5 rounded-lg text-sm outline-none"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('en', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="bg-[#222] border border-[#2b2b2b] text-white px-3 py-1.5 rounded-lg text-sm outline-none"
                  >
                    {[2025, 2026, 2027, 2028].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {loadingLog ? (
                <div className="flex justify-center py-12">
                  <div className="w-8 h-8 border-4 border-lime-400/30 border-t-lime-400 rounded-full animate-spin"></div>
                </div>
              ) : logData.entries.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="mx-auto w-10 h-10 mb-3 text-gray-600" />
                  <p className="text-sm">No expenses logged for this month</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-[#2b2b2b] text-gray-400 text-xs font-semibold">
                        <th className="pb-3">Date</th>
                        <th className="pb-3">Category</th>
                        <th className="pb-3">Note</th>
                        <th className="pb-3 text-right">Amount</th>
                        <th className="pb-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2b2b2b]">
                      {logData.entries.map((item) => (
                        <tr key={item.id} className="text-sm text-gray-300 hover:bg-[#222]/30 transition-all">
                          <td className="py-3.5 pr-2">
                            {new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </td>
                          <td className="py-3.5">
                            <span className="bg-[#222] border border-[#2b2b2b] text-white px-2 py-0.5 rounded-md text-xs font-medium">
                              {item.category}
                            </span>
                          </td>
                          <td className="py-3.5 max-w-[200px] truncate text-xs text-gray-400" title={item.note}>
                            {item.note || '-'}
                          </td>
                          <td className="py-3.5 text-right font-bold text-white">
                            ₹{item.amount.toLocaleString('en-IN')}
                          </td>
                          <td className="py-3.5 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={() => openEditModal(item)}
                                className="p-1 hover:bg-[#2b2b2b] rounded transition-all text-gray-400 hover:text-lime-400"
                                title="Edit"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => openDeleteModal(item)}
                                className="p-1 hover:bg-[#2b2b2b] rounded transition-all text-gray-400 hover:text-red-400"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right col: monthly total & category breakdown */}
          <div className="space-y-6">
            {/* Total Card */}
            <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-lime-400/5 blur-2xl rounded-full"></div>
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Total Outgoings</p>
              <h2 className="text-3xl font-extrabold text-lime-400 mt-2">
                ₹{logData.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                for {new Date(0, selectedMonth - 1).toLocaleString('en', { month: 'long' })} {selectedYear}
              </p>
            </div>

            {/* Category breakdown Card */}
            <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 shadow-xl">
              <h3 className="font-bold text-white mb-4 text-base">Category Breakdown</h3>
              {Object.keys(logData.categoryBreakdown).length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No categories to display</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(logData.categoryBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, amount]) => {
                      const percentage = logData.total > 0 ? (amount / logData.total) * 100 : 0;
                      return (
                        <div key={category} className="space-y-1.5">
                          <div className="flex justify-between text-xs text-gray-300">
                            <span className="font-medium">{category}</span>
                            <span className="font-bold">₹{amount.toLocaleString('en-IN')} ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-[#222] h-2 rounded-full overflow-hidden">
                            <div className="bg-lime-400 h-full rounded-full" style={{ width: `${percentage}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Pending and Rejected Review Queue */
        <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl p-6 shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-white text-lg">
              {activeSegment === 'pending' ? 'Pending Trainer Submissions' : 'Rejected Submissions'}
            </h3>
          </div>

          {loadingQueue ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-lime-400/30 border-t-lime-400 rounded-full animate-spin"></div>
            </div>
          ) : queueEntries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Receipt className="mx-auto w-10 h-10 mb-3 text-gray-600" />
              <p className="text-sm">No items in this queue</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#2b2b2b] text-gray-400 text-xs font-semibold">
                    <th className="pb-3">Trainer</th>
                    <th className="pb-3">Category</th>
                    <th className="pb-3">Date</th>
                    <th className="pb-3">Note</th>
                    <th className="pb-3 text-right">Amount</th>
                    <th className="pb-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2b2b2b]">
                  {queueEntries.map((item) => (
                    <tr key={item.id} className="text-sm text-gray-300 hover:bg-[#222]/30 transition-all">
                      <td className="py-3.5 pr-2 font-medium text-white">{item.createdByUsername}</td>
                      <td className="py-3.5">
                        <span className="bg-[#222] border border-[#2b2b2b] text-white px-2 py-0.5 rounded-md text-xs font-medium">
                          {item.category}
                        </span>
                      </td>
                      <td className="py-3.5 text-xs text-gray-400">
                        {new Date(item.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3.5 max-w-[200px] truncate text-xs text-gray-400" title={item.note}>
                        {item.note || '-'}
                      </td>
                      <td className="py-3.5 text-right font-bold text-white">
                        ₹{item.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="py-3.5 text-right">
                        {activeSegment === 'pending' ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleAction(item.id, 'accept')}
                              className="p-1 bg-[#222] border border-[#2b2b2b] hover:border-lime-400 rounded-md transition-all text-lime-400 cursor-pointer"
                              title="Accept & Log"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleAction(item.id, 'reject')}
                              className="p-1 bg-[#222] border border-[#2b2b2b] hover:border-red-500 rounded-md transition-all text-red-400 cursor-pointer"
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAction(item.id, 'accept')}
                            className="flex items-center gap-1.5 bg-[#222] border border-[#2b2b2b] hover:border-lime-400 text-lime-400 text-xs px-2.5 py-1.5 rounded-lg transition-all ml-auto cursor-pointer"
                            title="Move to Confirmed"
                          >
                            <RefreshCw size={12} />
                            <span>Approve Submission</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setAddModalOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-white">Add Gym Expense</h3>
              <button onClick={() => setAddModalOpen(false)} className="p-1 hover:bg-[#2b2b2b] rounded-lg transition-all">
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            {formError && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg text-sm mb-4">
                {formError}
              </div>
            )}

            <form onSubmit={handleAddSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Category</label>
                <select
                  required
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                >
                  <option value="" disabled>Select a category</option>
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Amount (₹)</label>
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Date</label>
                <input
                  required
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Note (Optional)</label>
                <textarea
                  rows="3"
                  placeholder="Details..."
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="px-5 py-2.5 text-sm rounded-lg bg-[#222] hover:bg-[#2b2b2b] text-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-lime-400 hover:bg-lime-300 text-zinc-950 transition-all shadow-lg disabled:opacity-50"
                >
                  {formSubmitting ? 'Saving...' : 'Save Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditModalOpen(false)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-white">Edit Expense</h3>
              <button onClick={() => setEditModalOpen(false)} className="p-1 hover:bg-[#2b2b2b] rounded-lg transition-all">
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            {formError && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-lg text-sm mb-4">
                {formError}
              </div>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Category</label>
                <select
                  required
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                >
                  {CATEGORIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Amount (₹)</label>
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Date</label>
                <input
                  required
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 font-semibold mb-1.5">Note (Optional)</label>
                <textarea
                  rows="3"
                  placeholder="Details..."
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  className="w-full bg-[#222] border border-[#2b2b2b] text-white px-4 py-3 rounded-xl focus:border-lime-400 outline-none transition-all text-sm resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className="px-5 py-2.5 text-sm rounded-lg bg-[#222] hover:bg-[#2b2b2b] text-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSubmitting}
                  className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-lime-400 hover:bg-lime-300 text-zinc-950 transition-all shadow-lg disabled:opacity-50"
                >
                  {formSubmitting ? 'Updating...' : 'Update Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-2xl text-center">
            <div className="mx-auto w-12 h-12 bg-red-500/10 border border-red-500/20 text-red-400 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle size={24} />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">Delete Expense?</h3>
            <p className="text-sm text-gray-400 mb-6">
              Are you sure you want to delete this expense of <strong className="text-white">₹{selectedExpense?.amount.toLocaleString('en-IN')}</strong>? This can't be undone.
            </p>
            <div className="flex justify-center gap-3">
              <button
                disabled={formSubmitting}
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 text-sm rounded-lg bg-[#222] hover:bg-[#2b2b2b] text-gray-300 transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                disabled={formSubmitting}
                onClick={handleDeleteSubmit}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white transition-all cursor-pointer"
              >
                {formSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesTab;
